import React, { useState, useEffect, useCallback } from 'react';
import {
  ChartColumn,
  FileText,
  Hand,
  Lock,
  PenLine,
  Settings,
  Star,
  Target,
  TriangleAlert,
} from 'lucide-react';
import { selectiveApi } from '../../services/selectiveApi';
import SessionLauncher from './SessionLauncher';
import QuestionDisplay from './QuestionDisplay';
import SessionResults from './SessionResults';
import WritingPractice from './writing/WritingPractice';
import ProgressDashboard from './progress/ProgressDashboard';
import AIConfigPanel from './AIConfigPanel';
import './SelectiveDashboard.css';

/**
 * SelectiveDashboard Component
 *
 * Main entry point for the NSW Selective High School Exam module.
 * Provides overview of student progress, recommendations, and quick access.
 *
 * Props:
 * - isAdmin: shows admin tools (AI configuration) when true
 * - yearLevel: student's year level (from profile; defaults to 6)
 */

// Map a mastery percentage to a proficiency bucket used by the mini matrix
const masteryToProficiency = (pct, hasUnlocked) => {
  if (!hasUnlocked) return 'locked';
  if (pct >= 75) return 'advanced';
  if (pct >= 50) return 'proficient';
  if (pct >= 25) return 'developing';
  return 'foundation';
};

// Compute per-skill overall mastery and unlock state from tiers
const summarizeSkill = (skill) => {
  const tiers = skill.tiers || [];
  const unlockedTiers = tiers.filter(t => t.status !== 'locked');
  const mastery = unlockedTiers.length > 0
    ? unlockedTiers.reduce((sum, t) => sum + (t.mastery_percentage || 0), 0) / unlockedTiers.length
    : 0;
  return {
    domainCode: skill.sub_skill_code,
    domainName: skill.sub_skill_name,
    mastery,
    hasUnlocked: unlockedTiers.length > 0,
    proficiency: masteryToProficiency(mastery, unlockedTiers.length > 0),
  };
};

const SelectiveDashboard = ({ isAdmin = false, yearLevel = 6 }) => {
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [activeSession, setActiveSession] = useState(null);
  const [launcherConfig, setLauncherConfig] = useState(null);
  const [completionResults, setCompletionResults] = useState(null);

  // Data states
  const [progressMatrix, setProgressMatrix] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);
  const [stats, setStats] = useState({
    totalSessions: 0,
    avgScore: 0,
    totalTimeMinutes: 0,
    masteryPercentage: 0,
  });

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch data in parallel
      const [matrixData, recommendationsData, sessionsData] = await Promise.all([
        selectiveApi.progress.getMatrix(yearLevel),
        selectiveApi.progress.getRecommendations({ limit: 5 }),
        selectiveApi.sessions.list({ limit: 10, status_filter: 'completed' })
      ]);

      setProgressMatrix(matrixData);
      setRecommendations(Array.isArray(recommendationsData) ? recommendationsData : []);
      setRecentSessions(Array.isArray(sessionsData) ? sessionsData : []);

      calculateStats(matrixData, sessionsData);

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [yearLevel]);

  // Fetch all dashboard data on mount
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const calculateStats = (matrix, sessions) => {
    const safeSessions = Array.isArray(sessions) ? sessions : [];
    const totalSessions = safeSessions.length;
    const avgScore = totalSessions > 0
      ? safeSessions.reduce((sum, s) => sum + (Number(s.percentage_score) || 0), 0) / totalSessions
      : 0;
    const totalTimeMinutes = Math.round(
      safeSessions.reduce((sum, s) => sum + (s.time_spent_seconds || 0), 0) / 60
    );

    setStats({
      totalSessions,
      avgScore: Math.round(avgScore),
      totalTimeMinutes,
      masteryPercentage: Math.round(matrix?.overall_mastery_percentage || 0),
    });
  };

  // Accepts an optional config (from recommendations / suggested sessions)
  // and pre-fills the launcher with it.
  const handleStartPractice = (config = null) => {
    setLauncherConfig(config && typeof config === 'object' ? config : null);
    setActiveTab('practice');
  };

  const handleStartMockExam = () => {
    setLauncherConfig(null);
    setActiveTab('mock-exam');
  };

  const handleViewProgress = () => {
    setActiveTab('progress');
  };

  const handleStartWriting = () => {
    setActiveTab('writing');
  };

  const handleOpenAIConfig = () => {
    setActiveTab('ai-config');
  };

  const handleBackToDashboard = () => {
    setActiveTab('overview');
    setLauncherConfig(null);
    fetchDashboardData(); // Refresh data when returning
  };

  const handleSessionStart = (session) => {
    setActiveSession(session);
    setCompletionResults(null);
    setActiveTab('question-display');
  };

  // completionResults carries tier_unlocks (only returned by the /complete call)
  const handleSessionComplete = (sessionId, results = null) => {
    setCompletionResults(results);
    setActiveTab('session-results');
  };

  // Loading state
  if (loading) {
    return (
      <div className="selective-dashboard loading">
        <div className="loading-spinner"></div>
        <p>Loading your Selective Exam dashboard...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="selective-dashboard error">
        <div className="error-message">
          <h3><TriangleAlert size={20} className="inline-icon" /> Error Loading Dashboard</h3>
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Render different views based on activeTab
  if (activeTab === 'practice' || activeTab === 'mock-exam') {
    return (
      <SessionLauncher
        initialSessionType={activeTab === 'mock-exam' ? 'mock_exam' : 'practice'}
        initialConfig={launcherConfig}
        onCancel={handleBackToDashboard}
        onSessionStart={handleSessionStart}
      />
    );
  }

  if (activeTab === 'question-display' && activeSession) {
    return (
      <QuestionDisplay
        session={activeSession}
        onComplete={handleSessionComplete}
        onError={(err) => {
          console.error('Session error:', err);
          setError(err.message);
          setActiveTab('overview');
        }}
      />
    );
  }

  if (activeTab === 'session-results' && activeSession) {
    return (
      <SessionResults
        sessionId={activeSession.session_id}
        initialResults={completionResults}
        onBackToDashboard={handleBackToDashboard}
        onStartNewSession={handleStartPractice}
      />
    );
  }

  if (activeTab === 'progress') {
    return (
      <ProgressDashboard
        onBack={handleBackToDashboard}
        onStartPractice={handleStartPractice}
        yearLevel={yearLevel}
      />
    );
  }

  if (activeTab === 'writing') {
    return <WritingPractice onBack={handleBackToDashboard} />;
  }

  if (activeTab === 'ai-config' && isAdmin) {
    return <AIConfigPanel onBack={handleBackToDashboard} />;
  }

  // Precompute skill summaries for section bars and the mini matrix
  const sections = progressMatrix?.sections || [];
  const sectionSummaries = sections.map(section => {
    const skillSummaries = (section.skills || []).map(summarizeSkill);
    const mastery = skillSummaries.length > 0
      ? skillSummaries.reduce((sum, s) => sum + s.mastery, 0) / skillSummaries.length
      : 0;
    return {
      sectionCode: section.section_code,
      sectionName: section.section_name,
      mastery: Math.round(mastery),
      unlockedSkills: skillSummaries.filter(s => s.hasUnlocked).length,
      totalSkills: skillSummaries.length,
      skills: skillSummaries,
    };
  });
  const allSkills = sectionSummaries.flatMap(s => s.skills);

  // Default: Overview Dashboard
  return (
    <div className="selective-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <h1>Selective Exam Preparation</h1>
        <p className="subtitle">Reading · Mathematical Reasoning · Thinking Skills · Writing</p>
      </header>

      {/* First-time welcome */}
      {stats.totalSessions === 0 && (
        <section className="welcome-banner">
          <div className="welcome-text">
            <h2><Hand size={22} className="inline-icon" /> Welcome! Ready for your first practice?</h2>
            <p>
              Try a short practice session — you'll get instant feedback on every answer,
              and your personal progress map will start filling in.
            </p>
          </div>
          <button className="welcome-cta" onClick={() => handleStartPractice()}>
            Start My First Session →
          </button>
        </section>
      )}

      {/* Quick Stats */}
      <section className="quick-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.totalSessions}</div>
          <div className="stat-label">Sessions Completed</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.avgScore}%</div>
          <div className="stat-label">Average Score</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.totalTimeMinutes}</div>
          <div className="stat-label">Minutes Practiced</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.masteryPercentage}%</div>
          <div className="stat-label">Mastery Level</div>
        </div>
      </section>

      {/* Main Content Grid */}
      <div className="dashboard-grid">

        {/* Left Column - Actions & Recommendations */}
        <aside className="left-column">

          {/* Quick Actions */}
          <div className="card quick-actions">
            <h2>Quick Start</h2>
            <button
              className="action-button primary"
              onClick={() => handleStartPractice()}
            >
              <FileText size={20} /> Start Practice Session
            </button>
            <button
              className="action-button secondary"
              onClick={handleStartMockExam}
            >
              <Target size={20} /> Take Mock Exam
            </button>
            <button
              className="action-button secondary"
              onClick={handleStartWriting}
            >
              <PenLine size={20} /> Practice Writing
            </button>
            <button
              className="action-button tertiary"
              onClick={handleViewProgress}
            >
              <ChartColumn size={20} /> View Detailed Progress
            </button>
          </div>

          {/* Admin Tools (admins only) */}
          {isAdmin && (
            <div className="card admin-tools">
              <h2>Admin Tools</h2>
              <button
                className="action-button admin"
                onClick={handleOpenAIConfig}
              >
                <Settings size={20} /> AI Configuration
              </button>
            </div>
          )}

          {/* Recommendations */}
          <div className="card recommendations">
            <h2>Recommended Practice Areas</h2>
            {recommendations.length === 0 ? (
              <p className="empty-state">
                Complete a session to get personalized recommendations!
              </p>
            ) : (
              <ul className="recommendation-list">
                {recommendations.map((rec, index) => (
                  <li key={index} className="recommendation-item">
                    <div className="rec-header">
                      <span className="rec-domain">{rec.domain_name}</span>
                      <span className={`rec-priority ${getPriorityInfo(rec.priority).className}`}>
                        {getPriorityInfo(rec.priority).label}
                      </span>
                    </div>
                    <div className="rec-reason">{rec.reason}</div>
                    <div className="rec-meta">
                      <span>Tier: {rec.recommended_tier}</span>
                      <span>~{rec.estimated_questions_to_master} questions to master</span>
                    </div>
                    <button
                      className="rec-start-button"
                      onClick={() => handleStartPractice({
                        domain_code: rec.domain_code,
                        tier: rec.recommended_tier,
                      })}
                    >
                      Practice this →
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* Right Column - Progress Overview */}
        <main className="right-column">

          {/* Progress Overview */}
          <div className="card progress-overview">
            <h2>Progress Overview</h2>

            {/* Section Progress Bars */}
            <div className="section-progress">
              {sectionSummaries.map(section => (
                <div key={section.sectionCode} className="section-bar">
                  <div className="section-header">
                    <span className="section-name">{section.sectionName}</span>
                    <span className="section-score">{section.mastery}%</span>
                  </div>
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar-fill"
                      style={{
                        width: `${section.mastery}%`,
                        backgroundColor: getProgressColor(section.mastery)
                      }}
                    />
                  </div>
                  <div className="section-details">
                    {section.unlockedSkills} / {section.totalSkills} skills unlocked
                  </div>
                </div>
              ))}
            </div>

            {/* Mini Matrix Visualization */}
            <div className="mini-matrix">
              <h3>Learning Matrix</h3>
              <div className="matrix-grid">
                {allSkills.slice(0, 28).map((skill, index) => (
                  <div
                    key={skill.domainCode || index}
                    className={`matrix-cell ${skill.proficiency}`}
                    title={`${skill.domainName} - ${Math.round(skill.mastery)}% mastery`}
                  >
                    {getCellIcon(skill.proficiency)}
                  </div>
                ))}
              </div>
              <div className="matrix-legend">
                <span><span className="legend-icon foundation">●</span> Foundation</span>
                <span><span className="legend-icon developing">●</span> Developing</span>
                <span><span className="legend-icon proficient">●</span> Proficient</span>
                <span><span className="legend-icon advanced">●</span> Advanced</span>
                <span><span className="legend-icon locked">●</span> Locked</span>
              </div>
            </div>
          </div>

          {/* Recent Sessions */}
          <div className="card recent-sessions">
            <h2>Recent Sessions</h2>
            {recentSessions.length === 0 ? (
              <p className="empty-state">
                No sessions yet. Start your first practice session above!
              </p>
            ) : (
              <div className="sessions-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Session</th>
                      <th>Score</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentSessions.map(session => (
                      <tr key={session.id}>
                        <td>{formatDate(session.completed_at || session.created_at)}</td>
                        <td>{formatSessionType(session.session_type)}</td>
                        <td>{session.session_name}</td>
                        <td className={getScoreClass(Number(session.percentage_score) || 0)}>
                          {Math.round(Number(session.percentage_score) || 0)}%
                        </td>
                        <td>{Math.round((session.time_spent_seconds || 0) / 60)} min</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

// Helper Functions
const getPriorityInfo = (priority) => {
  if (priority >= 80) return { label: 'Focus here first', className: 'priority-high' };
  if (priority >= 60) return { label: 'Worth practising', className: 'priority-medium' };
  return { label: 'Nice to improve', className: 'priority-low' };
};

const getProgressColor = (percentage) => {
  if (percentage >= 75) return '#22c55e'; // Green
  if (percentage >= 50) return '#eab308'; // Yellow
  if (percentage >= 25) return '#f97316'; // Orange
  return '#ef4444'; // Red
};

const getCellIcon = (proficiency) => {
  switch (proficiency) {
    case 'advanced': return <Star size={14} className="inline-icon" />;
    case 'proficient': return '✓';
    case 'developing': return '○';
    case 'foundation': return '·';
    default: return <Lock size={12} className="inline-icon" />;
  }
};

const getScoreClass = (score) => {
  if (score >= 80) return 'score-excellent';
  if (score >= 60) return 'score-good';
  if (score >= 40) return 'score-fair';
  return 'score-poor';
};

const formatSessionType = (type) => {
  const labels = {
    timed_practice: 'Practice',
    topic_focus: 'Topic Focus',
    mixed_review: 'Mixed Review',
    mock_exam: 'Mock Exam',
  };
  return labels[type] || type;
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

export default SelectiveDashboard;
